<template>
  <div :class="['additional-info', { top: isTop }]">
    <button class="button-icon button-icon-small button-rounded button-dim" @click="copyText">
      <CopyIcon class="icon-small icon-dimmer" />
    </button>

    <button
      v-if="iconAudio && (isReceivedAudio || isDownloading) && isEnabledAudioPlay"
      class="button-icon button-icon-small button-rounded button-dim"
      @click="playAudio"
    >
      <component class="icon-small icon-dimmer" :is="iconAudio" />
    </button>

    <SoundLoadingIcon v-if="!isReceivedAudio && !isDownloading && isEnabledAudioPlay" />

    <SoundWaveIcon class="right" v-if="isPlayingAudio" @click="playAudio" />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { storeToRefs } from 'pinia';

import CopyIcon from '@/components/icons/IconCopy.vue';
import IconSound from '@/components/icons/IconSound.vue';
import SoundLoadingIcon from '@/components/icons/IconSoundLoading.vue';
import SoundWaveIcon from '@/components/icons/IconSoundWave.vue';
import SpinnerIcon from '@/components/icons/IconSpinner.vue';
import { useAudioStore } from '@/stores/audio';
import { useToastStore } from '@/stores/toast.js';
import { ChatMessage, MessageText } from '@/types/socket';

const { t } = useI18n();

const { isDownloading, isTop, messageContents, isLastIsOwn, isTarget, message } = defineProps<{
  isDownloading: boolean;
  showLoading: boolean;
  isPlayingAudio: boolean;
  isTop: boolean;
  messageContents: MessageText[];
  isLastIsOwn: boolean;
  isTarget: boolean;
  message: ChatMessage;
  playAudio: () => void;
}>();

const toastStore = useToastStore();
const audioStore = useAudioStore();

const { languageOptions } = storeToRefs(audioStore);
const autoCompleteSecondsLeft = ref(0);
let autoCompleteInterval = 0;

const isEnabledAudioPlay = computed<boolean>(() => {
  const languageArray = isTarget ? [...message.tgtLanguageSet] : [...message.srcLanguageSet];

  return languageArray.every((lang) => languageOptions.value[lang].voiceOutputEnabled);
});

// 오디오 수신 여부
const isReceivedAudio = computed(() => {
  if (!isLastIsOwn) return true;
  if (autoCompleteSecondsLeft.value < 1) return true;
  if (!isEnabledAudioPlay.value) return false;

  const flag = isTarget ? 'tgt' : 'src';
  const isAudioComplete = message[flag].isAudioComplete === true;
  const isReset = message.reset === '1';

  return isAudioComplete || isReset;
});

// 수동재생 버튼 아이콘
const iconAudio = computed(() => {
  if (!isEnabledAudioPlay.value) return null;

  return isDownloading ? SpinnerIcon : isReceivedAudio.value ? IconSound : SoundLoadingIcon;
});

// 텍스트 복사
const copyText = () => {
  const text = messageContents.map((item: MessageText) => item.messageEdited || item.content).join('');
  navigator.clipboard.writeText(text);
  toastStore.info(t('copied'));
};

// 5초 뒤 무조건 로딩 종료
const resetAutoComplete = () => {
  autoCompleteSecondsLeft.value = 5;
  if (autoCompleteInterval) clearInterval(autoCompleteInterval);
  autoCompleteInterval = setInterval(() => {
    autoCompleteSecondsLeft.value--;
    if (autoCompleteSecondsLeft.value === 0) clearInterval(autoCompleteInterval);
  }, 1000);
};

// 메세지 변경 후 5초 내 로딩 종료
watch(
  () => (isTarget ? message.tgt.contents : message.src.contents),
  () => resetAutoComplete()
);
</script>

<style scoped lang="scss">
.additional-info {
  position: absolute;
  display: flex;
  justify-content: flex-end;
  height: fit-content;
  bottom: 1.25rem;
  gap: 0.25rem;
}

.top {
  position: absolute;
  top: 0.25rem;
}

.right {
  right: 0;
}
</style>
